// src/pages/admin/StationManage.js
// 后台 · 管理中心 · 站点管理（doc01 3.1，缺陷 112）
// 结构：仅 2 个 tab（所属站点 / 授权站点）+ 新增站点。
//  筛选置于表格上方：站点名称(模糊) / 状态(全部·显示·隐藏) + 查询·重置。
//  列：站点名称｜站点位置｜投运时间｜装机功率/kW｜站点容量/kWh｜显隐状态｜操作（授权站点额外：授权来源公司）。
//  操作：管理（进入单站监控）｜隐藏/显示（隐藏不计入云平台统计）｜站点标签（多选，≤10）。
//  说明：文档不存在「标牌管理」「站点配置」，此处已一并去除；标签为行内「站点标签」按钮，不再作为独立 tab。
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Table, Tag, Button, Select, Input, Modal, Space, message } from 'antd';
import ScrollTabs from '../../components/ScrollTabs';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import PageSkeleton from '../../components/PageSkeleton';
import { findMenu, pick } from '../../config/menuConfig';
import { OWN_STATIONS, AUTHORIZED_STATIONS, STATION_TAGS } from '../../config/stationData';

const labelStyle = { color: 'rgba(255,255,255,0.55)', fontSize: 12 };
const toTagArr = (v) => (Array.isArray(v) ? v : v ? String(v).split(' / ').map((x) => x.trim()).filter(Boolean) : []);

const StationManage = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const meta = findMenu('/app/admin/station');

  const [own, setOwn] = useState(() => OWN_STATIONS.map((s) => ({ ...s, tags: toTagArr(s.tags) })));
  const [auth, setAuth] = useState(() => AUTHORIZED_STATIONS.map((s) => ({ ...s, tags: toTagArr(s.tags) })));
  const [tab, setTab] = useState('own');

  const [fName, setFName] = useState('');
  const [fVisible, setFVisible] = useState(undefined);
  const [applied, setApplied] = useState({ name: '', visible: undefined });

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', location: '', onlineAt: '', power: '', capacity: '' });
  const [tagRow, setTagRow] = useState(null);
  const [tagSel, setTagSel] = useState([]);

  const setters = { own: setOwn, auth: setAuth };

  const doQuery = () => { setApplied({ name: fName, visible: fVisible }); message.success('已按条件查询'); };
  const doReset = () => { setFName(''); setFVisible(undefined); setApplied({ name: '', visible: undefined }); message.success('查询条件已重置'); };

  const toggleVisible = (kind, r) => {
    setters[kind]((list) => list.map((x) => (x.key === r.key ? { ...x, visible: x.visible === '隐藏' ? '显示' : '隐藏' } : x)));
    message.success(`「${r.name}」已${r.visible === '隐藏' ? '显示' : '隐藏'}（隐藏站点不计入云平台统计）`);
  };
  const openTags = (kind, r) => { setTagRow({ kind, ...r }); setTagSel(r.tags); };
  const saveTags = () => {
    const { kind, key, name } = tagRow;
    setters[kind]((list) => list.map((x) => (x.key === key ? { ...x, tags: tagSel } : x)));
    message.success(`「${name}」站点标签已保存（${tagSel.length} 个）`);
    setTagRow(null);
  };
  const openAdd = () => { setAddForm({ name: '', location: '', onlineAt: '', power: '', capacity: '' }); setAddOpen(true); };
  const submitAdd = () => {
    const name = addForm.name.trim();
    if (!name) { message.warning('请输入站点名称'); return; }
    setOwn((list) => [...list, {
      key: `own-${Date.now()}`, name, typeLabel: '储能电站', location: addForm.location.trim() || '—',
      power: addForm.power.trim() || '—', capacity: addForm.capacity.trim() || '—',
      onlineAt: addForm.onlineAt.trim() || '—', visible: '显示', tags: [],
    }]);
    message.success(`站点「${name}」已新增，可在单站监控中完善建站信息`);
    setAddOpen(false);
  };

  const nameCell = (r) => (
    <div>
      <div style={{ color: '#00d4ff' }}>{r.name}</div>
      {r.tags && r.tags.length ? (
        <div style={{ marginTop: 4 }}>
          {r.tags.map((t) => <Tag key={t} color="blue" style={{ margin: '2px 4px 0 0' }}>{t}</Tag>)}
        </div>
      ) : null}
    </div>
  );

  const filterRow = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 10 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={labelStyle}>站点名称</span>
        <Input size="small" value={fName} onChange={(e) => setFName(e.target.value)} placeholder="输入站点名称模糊查询" prefix={<SearchOutlined />} style={{ width: 220 }} allowClear />
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={labelStyle}>状态</span>
        <Select size="small" value={fVisible} onChange={setFVisible} placeholder="全部" allowClear style={{ width: 120 }}
          options={[{ value: '显示', label: '显示' }, { value: '隐藏', label: '隐藏' }]} />
      </span>
      <Space>
        <Button type="primary" size="small" onClick={doQuery}>查询</Button>
        <Button size="small" onClick={doReset}>重置</Button>
      </Space>
    </div>
  );

  const renderTab = (kind) => {
    const list = kind === 'own' ? own : auth;
    const filtered = list.filter((r) => {
      if (applied.name && !r.name.includes(applied.name.trim())) return false;
      if (applied.visible && r.visible !== applied.visible) return false;
      return true;
    });
    const columns = [
      { title: '站点名称', dataIndex: 'name', render: (_, r) => nameCell(r) },
      { title: '站点位置', dataIndex: 'location' },
      { title: '投运时间', dataIndex: 'onlineAt', width: 120 },
      { title: '装机功率/kW', dataIndex: 'power' },
      { title: '站点容量/kWh', dataIndex: 'capacity' },
      { title: '显隐状态', dataIndex: 'visible', width: 100, render: (v) => <Tag color={v === '隐藏' ? 'default' : 'green'} style={{ margin: 0 }}>{v}</Tag> },
      ...(kind === 'auth' ? [{ title: '授权来源公司', dataIndex: 'source' }] : []),
      {
        title: '操作', key: 'op', fixed: 'right', width: 210,
        render: (_, r) => (
          <Space size={2} wrap>
            <Button type="link" size="small" onClick={() => navigate('/app/monitor/station')}>管理</Button>
            <Button type="link" size="small" onClick={() => toggleVisible(kind, r)}>{r.visible === '隐藏' ? '显示' : '隐藏'}</Button>
            <Button type="link" size="small" onClick={() => openTags(kind, r)}>站点标签</Button>
          </Space>
        ),
      },
    ];
    return (
      <>
        {filterRow}
        {kind === 'own' ? (
          <div style={{ marginBottom: 12 }}>
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openAdd}>新增站点</Button>
          </div>
        ) : null}
        <Table rowKey="key" dataSource={filtered} columns={columns} pagination={{ pageSize: 10, size: 'small' }} size="middle" scroll={{ x: 1200 }} />
      </>
    );
  };

  return (
    <PageSkeleton
      title={meta ? pick(meta.label, lang) : '站点管理'}
      desc={meta ? pick(meta.desc, lang) : ''}
      crumb={meta ? [pick(meta.groupLabel, lang), pick(meta.sectionLabel, lang), pick(meta.label, lang)] : []}
      filters={[]}
      actions={[]}
    >
      <div className="panel blk-tabs">
        <ScrollTabs activeKey={tab} onChange={setTab} items={[
          { key: 'own', label: `所属站点 (${own.length})`, children: renderTab('own') },
          { key: 'auth', label: `授权站点 (${auth.length})`, children: renderTab('auth') },
        ]} />
      </div>

      <Modal title="新增站点" open={addOpen} onOk={submitAdd} onCancel={() => setAddOpen(false)} okText="确定" cancelText="取消" destroyOnHidden>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div><div style={labelStyle}>站点名称 *</div><Input value={addForm.name} onChange={(e) => setAddForm((s) => ({ ...s, name: e.target.value }))} placeholder="请输入站点名称" /></div>
          <div><div style={labelStyle}>站点位置</div><Input value={addForm.location} onChange={(e) => setAddForm((s) => ({ ...s, location: e.target.value }))} placeholder="请输入站点位置" /></div>
          <div><div style={labelStyle}>投运时间</div><Input value={addForm.onlineAt} onChange={(e) => setAddForm((s) => ({ ...s, onlineAt: e.target.value }))} placeholder="YYYY-MM-DD" /></div>
          <div><div style={labelStyle}>装机功率/kW</div><Input value={addForm.power} onChange={(e) => setAddForm((s) => ({ ...s, power: e.target.value }))} placeholder="如 1.25 MW" /></div>
          <div><div style={labelStyle}>站点容量/kWh</div><Input value={addForm.capacity} onChange={(e) => setAddForm((s) => ({ ...s, capacity: e.target.value }))} placeholder="如 2.50 MWh" /></div>
        </div>
      </Modal>

      <Modal title={`站点标签 · ${tagRow ? tagRow.name : ''}`} open={!!tagRow} onOk={saveTags} onCancel={() => setTagRow(null)} okText="确定" cancelText="取消" destroyOnHidden>
        <div style={{ marginBottom: 8, color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>给该站点配置标签，可多选，至多 10 个标签。</div>
        <Select
          mode="multiple"
          maxCount={10}
          value={tagSel}
          onChange={(v) => setTagSel(v.slice(0, 10))}
          style={{ width: '100%' }}
          placeholder="选择站点标签"
          options={STATION_TAGS.map((t) => ({ value: t.name, label: `${t.name}（${t.count}）` }))}
        />
      </Modal>
    </PageSkeleton>
  );
};

export default StationManage;
